window.global = window.global || window
window.module = window.module || { hot: null }
window.process = window.process || { env: {} }
window.App = window.App || {}

// Global styles (body, headings, antd overrides) that the devise/plain/payment
// layouts got through the retired account app's eagerly globbed tier1 page.
import '@javascript/styles/legacy-global.sass'
import { GetDashboardURL } from '@javascript/helpers/Helpers.js'
import rootSaga from '@javascript/sagas/index.js'
import configureStore from '@javascript/store/configureStore.js'
import * as Sentry from '@sentry/react'
import { Integrations } from '@sentry/tracing'
import { currentUrlHasSensitiveParams, maskSensitiveParams } from '@shared/sensitiveParams'
import ActionCable from 'actioncable'
import posthog from 'posthog-js'
import classify from 'underscore.string/classify'

if (App.State.Environment !== 'development' && App.State.Environment !== 'test') {
  Sentry.init({
    dsn:
      'https://46680741ea474e8aaec5de8e8e37588a@o301308.ingest.sentry.io/6461367',
    environment: App.State.Environment,
    release: App.State.Release,
    autoSessionTracking: true,
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 0,
  })
}

// Devise auth links carry their token in the query string, and posthog-js reads
// `location.href` / `document.referrer` on every event regardless of
// `capture_pageview`. Scrubbing here rather than via
// `mask_personal_data_properties` keeps ad-click IDs (gclid, fbclid, ...)
// intact for paid-ads attribution.
const scrubProperties = (properties) =>
  Object.fromEntries(
    Object.entries(properties).map(([key, value]) => [
      key,
      typeof value === 'string' ? maskSensitiveParams(value) : value,
    ])
  )

// posthog-js puts its own URL-bearing person properties on the event's top-level
// `$set_once`, but a `capture(event, { $set: ... })` call site would nest them
// under `properties` instead, so both locations are scrubbed.
const scrubPersonProperties = (properties) => {
  const scrubbed = scrubProperties(properties)

  for (const key of ['$set', '$set_once']) {
    if (scrubbed[key]) scrubbed[key] = scrubProperties(scrubbed[key])
  }

  return scrubbed
}

const scrubSensitiveParams = (event) => {
  if (!event) return event

  const sanitized = { ...event }

  if (sanitized.properties) sanitized.properties = scrubPersonProperties(sanitized.properties)

  for (const key of ['$set', '$set_once']) {
    if (sanitized[key]) sanitized[key] = scrubProperties(sanitized[key])
  }

  return sanitized
}

if (App.State.Environment !== 'development' && App.State.Environment !== 'test' && App.State.posthog?.apiKey) {
  posthog.init(App.State.posthog.apiKey, {
    api_host: App.State.posthog.host || 'https://eu.i.posthog.com',
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    before_send: scrubSensitiveParams,
    // Replay snapshots embed the page URL inside `$snapshot_data`, which
    // `before_send` cannot reach, so replay stays off while a token is in the URL.
    disable_session_recording: currentUrlHasSensitiveParams(),
  })
}

App.Store = configureStore()

App.Store.runSaga(rootSaga)

App.Cable = ActionCable.createConsumer(GetDashboardURL('/cable', { ws: true }))

App.Debug = (...messages) => {
  if (
    App.State.Environment == 'staging' ||
    App.State.Environment == 'integration' ||
    App.State.Environment == 'development'
  ) {
    // eslint-disable-next-line no-console
    console.warn(...messages)
  }
}

import.meta.glob('./sessions/**/*.js', { eager: true })
import.meta.glob('./otp_session/**/*.js', { eager: true })
import.meta.glob('./registrations/**/*.js', { eager: true })
import.meta.glob('./oauth_profile/**/*.js', { eager: true })
import.meta.glob('./unlocks/**/*.js', { eager: true })
import.meta.glob('./merchant_refunds_confirmation/**/*.js', { eager: true })
import.meta.glob('./bills/**/*.js', { eager: true })

const garberIrish = () => {
  const body = document.body
  if (!body) return

  const controller_path = body.getAttribute('data-controller')
  const action = body.getAttribute('data-action')

  if (controller_path && action) {
    const className = classify(
      controller_path.replace(/\//g, '_') + `_${action}`
    )

    if (App.Pages[className]) {
      const klass = eval(`new App.Pages.${className}`)

      if (klass.init) {
        klass.init()
      }
    }

    // console.log(className)
    // console.log('Registered Pages:', Object.keys(window.App.Pages))
  }
}

document.addEventListener('DOMContentLoaded', garberIrish)
